import React, { useState } from 'react';
import { TrainingData } from './TrainingWizard';
import { UserIcon, CheckIcon, XIcon, AlertTriangleIcon, ClockIcon, CalendarIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
// Mock data for instructors
const mockInstructors = [{
  id: 1,
  name: 'Erik de Vries',
  email: 'e.devries@vvgn.nl',
  phone: '06-12345678',
  specialties: ['Sliptraining Basis', 'Sliptraining Gevorderd', 'Calamiteitentraining'],
  available: true,
  rating: 9.2
}, {
  id: 2,
  name: 'Lisa Bakker',
  email: 'l.bakker@vvgn.nl',
  phone: '06-23456789',
  specialties: ['Defensief Rijden', 'Code 95 Training'],
  available: true,
  rating: 9.0
}, {
  id: 3,
  name: 'Peter Jansen',
  email: 'p.jansen@vvgn.nl',
  phone: '06-34567890',
  specialties: ['Sliptraining Basis', 'Defensief Rijden', 'Code 95 Training'],
  available: false,
  rating: 8.8,
  unavailabilityReason: 'Ziek gemeld tot 25 juli'
}, {
  id: 4,
  name: 'Maria Visser',
  email: 'm.visser@vvgn.nl',
  phone: '06-45678901',
  specialties: ['Sliptraining Basis', 'Defensief Rijden'],
  available: true,
  rating: 8.5
}, {
  id: 5,
  name: 'Thomas Berg',
  email: 't.berg@vvgn.nl',
  phone: '06-56789012',
  specialties: ['Sliptraining Gevorderd', 'Calamiteitentraining'],
  available: true,
  rating: 8.9
}];
interface InstructorStepProps {
  trainingData: TrainingData;
  updateTrainingData: (data: Partial<TrainingData>) => void;
}
export function InstructorStep({
  trainingData,
  updateTrainingData
}: InstructorStepProps) {
  const [showUnavailableInstructors, setShowUnavailableInstructors] = useState(false);
  const handleInstructorSelect = (instructorId: number) => {
    const selectedInstructor = mockInstructors.find(instructor => instructor.id === instructorId);
    if (selectedInstructor) {
      updateTrainingData({
        instructorId,
        instructorName: selectedInstructor.name
      });
    }
  };
  // Filter instructors based on availability and training type
  const getFilteredInstructors = () => {
    const allInstructors = mockInstructors.filter(instructor => {
      // If training type is selected, check if instructor is specialized in it
      if (trainingData.trainingTypeName) {
        return instructor.specialties.includes(trainingData.trainingTypeName);
      }
      return true;
    });
    // Sort available instructors first, then by rating
    return allInstructors.sort((a, b) => {
      if (a.available && !b.available) return -1;
      if (!a.available && b.available) return 1;
      return b.rating - a.rating;
    });
  };
  const filteredInstructors = getFilteredInstructors();
  const availableInstructors = filteredInstructors.filter(instructor => instructor.available);
  const unavailableInstructors = filteredInstructors.filter(instructor => !instructor.available);
  return <div data-id="element-3166">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3167">
        Selecteer Instructeur
      </h2>

      {trainingData.trainingTypeName && trainingData.startDate && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6" data-id="element-3168">
          <h3 className="font-medium text-gray-900 mb-2" data-id="element-3169">
            Trainingsinformatie
          </h3>
          <div className="space-y-2 text-sm" data-id="element-3170">
            <div className="flex items-center" data-id="element-3171">
              <div className="w-24 text-gray-500" data-id="element-3172">Type:</div>
              <div className="font-medium" data-id="element-3173">{trainingData.trainingTypeName}</div>
            </div>
            <div className="flex items-center" data-id="element-3174">
              <div className="w-24 text-gray-500" data-id="element-3175">Datum:</div>
              <div className="flex items-center" data-id="element-3176">
                <CalendarIcon className="h-4 w-4 mr-1" data-id="element-3177" />
                <span data-id="element-3178">
                  {new Date(trainingData.startDate).toLocaleDateString('nl-NL', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
                </span>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3179">
              <div className="w-24 text-gray-500" data-id="element-3180">Tijd:</div>
              <div className="flex items-center" data-id="element-3181">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-3182" />
                <span data-id="element-3183">
                  {new Date(trainingData.startDate).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                  -{' '}
                  {new Date(trainingData.endDate).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </span>
              </div>
            </div>
          </div>
        </div>}

      <div className="space-y-4" data-id="element-3184">
        {availableInstructors.length > 0 ? availableInstructors.map(instructor => <div key={instructor.id} className={`p-4 border rounded-lg cursor-pointer transition ${trainingData.instructorId === instructor.id ? 'border-2 border-[#B61C1C] bg-red-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} onClick={() => handleInstructorSelect(instructor.id)} data-id="element-3185">
              <div className="flex justify-between items-start" data-id="element-3186">
                <div className="flex items-start" data-id="element-3187">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3188">
                    <UserIcon className="h-5 w-5 text-gray-600" data-id="element-3189" />
                  </div>
                  <div className="ml-3" data-id="element-3190">
                    <h4 className="font-medium text-gray-900" data-id="element-3191">
                      {instructor.name}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-3192">{instructor.email}</p>
                    <div className="flex items-center mt-1" data-id="element-3193">
                      <Badge variant="success" className="mr-2" data-id="element-3194">
                        Beschikbaar
                      </Badge>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-3195">
                        <span className="font-medium" data-id="element-3196">{instructor.rating}</span>
                        <div className="ml-1 flex" data-id="element-3197">
                          {Array.from({
                      length: 5
                    }).map((_, i) => <div key={i} className={`h-1 w-4 ${i < Math.floor(instructor.rating) ? 'bg-[#B61C1C]' : 'bg-gray-200'} ${i > 0 ? 'ml-0.5' : ''}`} data-id="element-3198"></div>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 justify-end max-w-[40%]" data-id="element-3199">
                  {instructor.specialties.map((specialty, index) => <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${specialty === trainingData.trainingTypeName ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3200">
                      {specialty === trainingData.trainingTypeName && <CheckIcon className="h-3 w-3 mr-1" data-id="element-3201" />}
                      {specialty}
                    </span>)}
                </div>
              </div>
            </div>) : <div className="text-center py-8 text-gray-500" data-id="element-3202">
            <AlertTriangleIcon className="h-10 w-10 mx-auto mb-2 text-yellow-500" data-id="element-3203" />
            <p className="mb-2" data-id="element-3204">
              Geen beschikbare instructeurs gevonden voor deze training.
            </p>
            <p className="text-sm" data-id="element-3205">
              Probeer een andere datum of training te selecteren.
            </p>
          </div>}

        {unavailableInstructors.length > 0 && <div className="mt-6" data-id="element-3206">
            <button type="button" className="flex items-center text-sm text-gray-600 hover:text-gray-900" onClick={() => setShowUnavailableInstructors(!showUnavailableInstructors)} data-id="element-3207">
              {showUnavailableInstructors ? <>
                  <XIcon className="h-4 w-4 mr-1" data-id="element-3208" />
                  Verberg niet-beschikbare instructeurs
                </> : <>
                  <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-3209" />
                  Toon ook niet-beschikbare instructeurs (
                  {unavailableInstructors.length})
                </>}
            </button>
            {showUnavailableInstructors && <div className="mt-4 space-y-4" data-id="element-3210">
                {unavailableInstructors.map(instructor => <div key={instructor.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50" data-id="element-3211">
                    <div className="flex justify-between items-start" data-id="element-3212">
                      <div className="flex items-start" data-id="element-3213">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3214">
                          <UserIcon className="h-5 w-5 text-gray-600" data-id="element-3215" />
                        </div>
                        <div className="ml-3" data-id="element-3216">
                          <h4 className="font-medium text-gray-900" data-id="element-3217">
                            {instructor.name}
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-3218">
                            {instructor.email}
                          </p>
                          <div className="flex items-center mt-1" data-id="element-3219">
                            <Badge variant="danger" className="mr-2" data-id="element-3220">
                              Niet Beschikbaar
                            </Badge>
                            <p className="text-xs text-gray-500" data-id="element-3221">
                              {instructor.unavailabilityReason}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end max-w-[40%]" data-id="element-3222">
                        {instructor.specialties.map((specialty, index) => <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${specialty === trainingData.trainingTypeName ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3223">
                            {specialty === trainingData.trainingTypeName && <CheckIcon className="h-3 w-3 mr-1" data-id="element-3224" />}
                            {specialty}
                          </span>)}
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>}
      </div>
    </div>;
}